import { type RefObject, useCallback, useEffect, useRef } from 'react';

const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
];

export const useFocusTrap = (isActive: boolean, modalRef: RefObject<HTMLElement>) => {
    const focusApplied = useRef(false);

    const getFocusable = useCallback(() => {
        if (!modalRef.current) return [];
        return Array.from(modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors.join(','))).filter((el) => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });
    }, [modalRef]);

    useEffect(() => {
        if (!isActive || !modalRef.current) return;

        let animationFrame: number;
        let timeout: number;

        const tryFocus = () => {
            const focusables = getFocusable();
            if (focusables.length > 0 && !focusApplied.current) {
                focusables[0].focus();
                focusApplied.current = true;
            } else {
                animationFrame = requestAnimationFrame(() => {
                    timeout = window.setTimeout(tryFocus, 10);
                });
            }
        };

        animationFrame = requestAnimationFrame(() => {
            timeout = window.setTimeout(tryFocus, 10);
        });

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            const focusables = getFocusable();
            if (focusables.length === 0) return;

            const first = focusables[0];
            const last = focusables[focusables.length - 1];

            if (e.shiftKey && document.activeElement === first) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
                e.preventDefault();
                first.focus();
            }
        };

        const node = modalRef.current;
        node.addEventListener('keydown', handleKeyDown);

        return () => {
            cancelAnimationFrame(animationFrame);
            clearTimeout(timeout);
            node.removeEventListener('keydown', handleKeyDown);
            focusApplied.current = false;
        };
    }, [isActive, modalRef, getFocusable]);
};
